/**
 * Invoice Modal
 *
 * Create/Edit invoice form modal with line items
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCreateInvoice, useUpdateInvoice } from '../../services/queries/invoiceQueries';
import { useProducts } from '../../services/queries/productQueries';
import { useEffectivePrice } from '../../services/queries/clientPriceQueries';
import { persistedClientRecords } from '../../services/mockData/clients';
import { useToast } from '../../contexts/ToastContext';
import { X, Plus, Trash2 } from 'lucide-react';
import type { Invoice, InvoiceLineItem } from '../../types/index';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  clientId?: string;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice,
  clientId: initialClientId
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!invoice;

  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  const { data: products } = useProducts();
  const allClients = persistedClientRecords.get();

  const [formData, setFormData] = useState({
    clientId: initialClientId || '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: ''
  });

  const [lineItems, setLineItems] = useState<Omit<InvoiceLineItem, 'id' | 'lineTotal'>[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (invoice) {
      setFormData({
        clientId: invoice.clientId,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        notes: invoice.notes || ''
      });
      setLineItems(invoice.lineItems.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
        description: item.description
      })));
    } else {
      setFormData({
        clientId: initialClientId || '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        notes: ''
      });
      setLineItems([]);
    }
    setErrors({});
  }, [invoice, initialClientId, isOpen]);

  // Set default due date (30 days from issue date)
  useEffect(() => {
    if (!isEditMode && formData.issueDate && !formData.dueDate) {
      const issueDate = new Date(formData.issueDate);
      issueDate.setDate(issueDate.getDate() + 30);
      setFormData(prev => ({
        ...prev,
        dueDate: issueDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.issueDate, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAddLineItem = () => {
    setLineItems([...lineItems, {
      productId: '',
      productName: '',
      productCode: '',
      quantity: 1,
      unitPrice: 0,
      discount: undefined,
      description: ''
    }]);
  };

  const handleLineItemChange = (index: number, field: string, value: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-fill product details when product is selected
    if (field === 'productId' && products) {
      const product = products.find(p => p.id === value);
      if (product) {
        updated[index] = {
          ...updated[index],
          productId: product.id,
          productName: product.name,
          productCode: product.productCode,
          unitPrice: product.unitPrice
        };
      }
    }
    
    // Calculate line total when quantity or price changes
    if (field === 'quantity' || field === 'unitPrice' || field === 'discount') {
      const item = updated[index];
      let total = item.unitPrice * item.quantity;
      if (item.discount) {
        total = total * (1 - item.discount / 100);
      }
      // Note: lineTotal will be calculated on save
    }
    
    setLineItems(updated);
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      let itemTotal = item.unitPrice * item.quantity;
      if (item.discount) {
        itemTotal = itemTotal * (1 - item.discount / 100);
      }
      return sum + itemTotal;
    }, 0);
    const taxAmount = 0; // Placeholder
    const totalAmount = subtotal + taxAmount;
    return { subtotal, taxAmount, totalAmount };
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }
    if (!formData.issueDate) {
      newErrors.issueDate = 'Issue date is required';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'At least one line item is required';
    }
    lineItems.forEach((item, index) => {
      if (!item.productId) {
        newErrors[`lineItem_${index}_product`] = 'Product is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`lineItem_${index}_quantity`] = 'Valid quantity is required';
      }
      if (!item.unitPrice || item.unitPrice < 0) {
        newErrors[`lineItem_${index}_price`] = 'Valid unit price is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    const selectedClient = allClients.find(c => c.id === formData.clientId);
    const clientName = selectedClient?.name || 'Unknown Client';

    const totals = calculateTotals();
    const invoiceLineItems: InvoiceLineItem[] = lineItems.map(item => {
      let lineTotal = item.unitPrice * item.quantity;
      if (item.discount) {
        lineTotal = lineTotal * (1 - item.discount / 100);
      }
      return {
        id: `li_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
        lineTotal,
        description: item.description
      };
    });

    const invoiceData = {
      clientId: formData.clientId,
      clientName,
      status: 'draft' as const,
      issueDate: formData.issueDate,
      dueDate: formData.dueDate,
      subtotal: totals.subtotal,
      taxAmount: totals.taxAmount,
      totalAmount: totals.totalAmount,
      currency: 'USD',
      notes: formData.notes.trim() || undefined,
      lineItems: invoiceLineItems
    };

    if (isEditMode && invoice) {
      updateInvoice.mutate(
        { id: invoice.id, updates: invoiceData },
        {
          onSuccess: () => {
            success('Invoice updated', `Invoice ${invoice.invoiceNumber} has been updated`);
            onClose();
          },
          onError: () => {
            showError('Error', 'Failed to update invoice');
          }
        }
      );
    } else {
      createInvoice.mutate(invoiceData, {
        onSuccess: () => {
          success('Invoice created', 'Invoice has been created');
          onClose();
        },
        onError: () => {
          showError('Error', 'Failed to create invoice');
        }
      });
    }
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-[#1e293b] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Invoice' : 'New Invoice'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Overview Section */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">Overview</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    disabled={!!initialClientId}
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                      errors.clientId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } ${initialClientId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select a client</option>
                    {allClients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && <p className="mt-1 text-sm text-red-500">{errors.clientId}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Issue Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="issueDate"
                      value={formData.issueDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                        errors.issueDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {errors.issueDate && <p className="mt-1 text-sm text-red-500">{errors.issueDate}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                        errors.dueDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {errors.dueDate && <p className="mt-1 text-sm text-red-500">{errors.dueDate}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Line Items Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Line Items</h4>
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>

                {errors.lineItems && <p className="text-sm text-red-500">{errors.lineItems}</p>}

                <div className="space-y-3">
                  {lineItems.map((item, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-[#334155] rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Item {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Product <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={item.productId}
                          onChange={(e) => handleLineItemChange(index, 'productId', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                            errors[`lineItem_${index}_product`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          <option value="">Select a product</option>
                          {products?.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.productCode})
                            </option>
                          ))}
                        </select>
                        {errors[`lineItem_${index}_product`] && (
                          <p className="mt-1 text-sm text-red-500">{errors[`lineItem_${index}_product`]}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Quantity <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, 'quantity', Number(e.target.value))}
                            min="1"
                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                              errors[`lineItem_${index}_quantity`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                            }`}
                          />
                          {errors[`lineItem_${index}_quantity`] && (
                            <p className="mt-1 text-sm text-red-500">{errors[`lineItem_${index}_quantity`]}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Unit Price <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleLineItemChange(index, 'unitPrice', Number(e.target.value))}
                              step="0.01"
                              min="0"
                              className={`w-full pl-8 pr-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                                errors[`lineItem_${index}_price`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                          </div>
                          {errors[`lineItem_${index}_price`] && (
                            <p className="mt-1 text-sm text-red-500">{errors[`lineItem_${index}_price`]}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Discount (%)
                          </label>
                          <input
                            type="number"
                            value={item.discount || ''}
                            onChange={(e) => handleLineItemChange(index, 'discount', e.target.value ? Number(e.target.value) : undefined)}
                            min="0"
                            max="100"
                            step="1"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description || ''}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {lineItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No line items added. Click "Add Item" to add products.</p>
                  </div>
                )}
              </div>

              {/* Totals Section */}
              {lineItems.length > 0 && (
                <div className="bg-gray-50 dark:bg-[#334155] rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Tax</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.taxAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-300 dark:border-gray-600 pt-2">
                      <span className="text-gray-900 dark:text-white">Total</span>
                      <span className="text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#334155] border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-[#475569] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createInvoice.isPending || updateInvoice.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createInvoice.isPending || updateInvoice.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Invoice'
                  : 'Create Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
